import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Paper, Typography, Button, CircularProgress, Alert, Stack, Chip,
} from '@mui/material';
import { Print, PictureAsPdf, GridOn, Refresh, Book } from '@mui/icons-material';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { reportReceiptsLedgerURL } from '../../BaseURL';

function headers() { return { Authorization: `Bearer ${localStorage.getItem('church_token')}` }; }

const fmt = (n) =>
  Number(n || 0).toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDateLong = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString('en-GH', { day: '2-digit', month: 'long', year: 'numeric' });
};

const fmtDateShort = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString('en-GH', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtDayName = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('en-GH', { weekday: 'short' });
};

// Church week: Sunday → Saturday
const startOfWeek = (d) => {
  const dt = new Date(d);
  dt.setHours(0, 0, 0, 0);
  const dow = dt.getDay(); // 0 = Sun
  dt.setDate(dt.getDate() - dow);
  return dt;
};

const endOfWeek = (d) => {
  const s = startOfWeek(d);
  const e = new Date(s);
  e.setDate(s.getDate() + 6);
  return e;
};

const isoDay = (d) => {
  const dt = new Date(d);
  dt.setHours(0, 0, 0, 0);
  return dt.toISOString().slice(0, 10);
};

const METHOD_LABEL = { cash: 'Cash', cheque: 'Cheque', transfer: 'Transfer', mobile_money: 'Mobile Money' };

const NAVY  = '#0B2545';
const GREEN = '#0F766E';
const RED   = '#B91C1C';
const GOLD  = '#C89211';
const RULE  = '#0B2545';

export default function WeeklyCashLog({ start, end }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const printRef = useRef(null);

  const load = () => {
    if (!start || !end) return;
    setLoading(true); setError('');
    axios.get(reportReceiptsLedgerURL, {
      headers: headers(),
      params: { start, end },
    })
      .then(r => { if (r.data.success) setData(r.data); else setError(r.data.message); })
      .catch(err => setError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end]);

  // Group receipts by week (Sun–Sat)
  const weeks = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    for (const r of data.receipts) {
      if (!r.income_date) continue;
      const sow = startOfWeek(r.income_date);
      const key = isoDay(sow);
      if (!map.has(key)) {
        map.set(key, {
          key,
          start_date: sow,
          end_date:   endOfWeek(sow),
          entries:    [],
          total:      0,
        });
      }
      const w = map.get(key);
      w.entries.push(r);
      w.total += Number(r.amount || 0);
    }
    return [...map.values()].sort((a, b) => a.start_date - b.start_date);
  }, [data]);

  const grandTotal = useMemo(() => weeks.reduce((s, w) => s + w.total, 0), [weeks]);
  const entryCount = useMemo(() => weeks.reduce((s, w) => s + w.entries.length, 0), [weeks]);

  // ─── PRINT ────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    if (!printRef.current) return;
    const w = window.open('', '_blank', 'width=1200,height=820');
    if (!w) return;
    w.document.write(`
      <html><head><title>Weekly Cash Log - ${data?.period.label || ''}</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: 'Helvetica Neue', -apple-system, Segoe UI, Roboto, Arial, sans-serif; margin: 28px 32px; color: #111; }
        .letterhead { display: flex; align-items: center; gap: 20px; padding-bottom: 14px; border-bottom: 3px double ${NAVY}; }
        .letterhead img { width: 84px; height: 84px; object-fit: contain; border-radius: 6px; }
        .letterhead .info h1 { margin: 0; font-size: 26px; letter-spacing: 0.02em; color: ${NAVY}; }
        .letterhead .info p { margin: 3px 0 0; color: #555; font-size: 12px; }
        .stmt-title { text-align: center; margin: 22px 0 4px; }
        .stmt-title .name { font-size: 15px; font-weight: 800; letter-spacing: 0.16em; color: ${NAVY}; border-top: 1.5px solid ${NAVY}; border-bottom: 1.5px solid ${NAVY}; display: inline-block; padding: 4px 24px; }
        .stmt-title .period { margin-top: 8px; font-size: 12px; color: #555; }
        table { width: 100%; border-collapse: collapse; margin-top: 14px; page-break-inside: auto; }
        thead th { text-align: left; font-size: 10.5px; letter-spacing: 0.06em; text-transform: uppercase; color: #fff; background: ${NAVY}; padding: 8px 10px; }
        thead th.right { text-align: right; }
        tbody td { padding: 6px 10px; font-size: 12px; border-bottom: 1px solid #eef0f4; }
        tbody td.right { text-align: right; font-variant-numeric: tabular-nums; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
        .week-head td { background: ${NAVY}; color: #fff; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; padding: 8px 10px; font-size: 11.5px; }
        .week-head .right { text-align: right; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
        .week-total td { border-top: 2px solid ${NAVY}; border-bottom: 2px solid ${NAVY}; font-weight: 800; color: ${NAVY}; background: #eef2f7; padding: 8px 10px; text-transform: uppercase; font-size: 12px; }
        .grand td { border-top: 3px double ${NAVY}; border-bottom: 3px double ${NAVY}; font-weight: 800; font-size: 14px; background: #fffdf5; padding: 12px 10px; text-transform: uppercase; letter-spacing: 0.08em; color: ${GREEN}; }
        .footer { display: flex; justify-content: space-between; margin-top: 30px; font-size: 10px; color: #888; }
        @page { size: A4 landscape; margin: 12mm; }
      </style>
      </head><body>${printRef.current.innerHTML}</body></html>
    `);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 300);
  };

  // ─── EXPORT PDF ───────────────────────────────────────────────────────────
  const handlePdf = () => {
    if (!data) return;
    const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 36;

    // Letterhead
    let y = 32;
    const logoSize = 52;
    let textLeft = margin;
    if (data.church?.logo && String(data.church.logo).startsWith('data:image')) {
      try {
        const fmtType = data.church.logo.substring(11, data.church.logo.indexOf(';'));
        doc.addImage(data.church.logo, fmtType.toUpperCase(), margin, y, logoSize, logoSize);
        textLeft = margin + logoSize + 12;
      } catch (e) { /* skip */ }
    }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(11, 37, 69);
    doc.text(data.church?.name || 'Church', textLeft, y + 18);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(90);
    const subInfo = [data.church?.address, data.church?.phone, data.church?.email].filter(Boolean).join('  ·  ');
    if (subInfo) doc.text(subInfo, textLeft, y + 32);
    y += Math.max(logoSize, 36) + 6;
    doc.setDrawColor(11, 37, 69); doc.setLineWidth(1.0);
    doc.line(margin, y, pageW - margin, y);
    doc.setLineWidth(0.4); doc.line(margin, y + 3, pageW - margin, y + 3);
    y += 16;

    // Title
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(11, 37, 69);
    const title = 'WEEKLY CASH LOG';
    const titleW = doc.getTextWidth(title);
    const tx = (pageW - titleW) / 2;
    doc.setLineWidth(0.6);
    doc.line(tx - 16, y - 4, tx + titleW + 16, y - 4);
    doc.text(title, pageW / 2, y + 10, { align: 'center' });
    doc.line(tx - 16, y + 15, tx + titleW + 15, y + 15);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(90);
    doc.text(
      `For the period ${fmtDateLong(data.period.start_date)} to ${fmtDateLong(data.period.end_date)}`,
      pageW / 2, y + 30, { align: 'center' }
    );
    y += 46;

    // Build body: weeks with headers, entries, subtotals
    const body = [];
    weeks.forEach((wk, wi) => {
      body.push([
        { content: `WEEK ${wi + 1} · ${fmtDateShort(wk.start_date)} — ${fmtDateShort(wk.end_date)}`,
          colSpan: 7, styles: { fillColor: [11, 37, 69], textColor: 255, fontStyle: 'bold' } },
        { content: `GH₵ ${fmt(wk.total)}`, styles: { fillColor: [11, 37, 69], textColor: 255, fontStyle: 'bold', halign: 'right' } },
      ]);
      wk.entries.forEach((r, i) => {
        body.push([
          String(i + 1),
          `${fmtDayName(r.income_date)}  ${fmtDateShort(r.income_date)}`,
          r.receipt_no || '—',
          r.income_type_name || '—',
          r.income_category || '—',
          r.narration || '—',
          METHOD_LABEL[r.payment_method] || r.payment_method || '—',
          { content: fmt(r.amount), styles: { halign: 'right' } },
        ]);
      });
      body.push([
        { content: `Weekly Total (${wk.entries.length} entr${wk.entries.length === 1 ? 'y' : 'ies'})`, colSpan: 7,
          styles: { fontStyle: 'bold', fillColor: [238, 242, 247], textColor: [11, 37, 69] } },
        { content: fmt(wk.total),
          styles: { halign: 'right', fontStyle: 'bold', fillColor: [238, 242, 247], textColor: [11, 37, 69] } },
      ]);
    });
    body.push([
      { content: `GRAND TOTAL (${entryCount} entries across ${weeks.length} week${weeks.length === 1 ? '' : 's'})`,
        colSpan: 7, styles: { fontStyle: 'bold', fillColor: [255, 253, 245], textColor: [15, 118, 110], fontSize: 11 } },
      { content: fmt(grandTotal),
        styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 253, 245], textColor: [15, 118, 110], fontSize: 11 } },
    ]);

    autoTable(doc, {
      startY: y,
      head: [[
        '#',
        'Day / Date',
        'Receipt No.',
        'Income Type',
        'Category',
        'Description',
        'Method',
        { content: 'Amount (GH₵)', styles: { halign: 'right' } },
      ]],
      body,
      theme: 'grid',
      styles: { fontSize: 8.5, cellPadding: 4, lineColor: [230, 232, 238], lineWidth: 0.4 },
      headStyles: { fillColor: [11, 37, 69], textColor: 255, fontStyle: 'bold', halign: 'left', valign: 'middle' },
      columnStyles: {
        0: { cellWidth: 26, halign: 'center' },
        1: { cellWidth: 90 },
        2: { cellWidth: 70 },
        3: { cellWidth: 100 },
        4: { cellWidth: 90 },
        5: { cellWidth: 'auto' },
        6: { cellWidth: 70 },
        7: { cellWidth: 80, halign: 'right' },
      },
      margin: { top: 40, left: margin, right: margin, bottom: 40 },
      didDrawPage: () => {
        doc.setFontSize(8); doc.setTextColor(120);
        doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageW - margin, pageH - 18, { align: 'right' });
        doc.text(`Generated ${new Date().toLocaleString('en-GH')}`, margin, pageH - 18);
      },
    });

    const stamp = `${data.period.start_date}_to_${data.period.end_date}`;
    doc.save(`Weekly_Cash_Log_${stamp.replace(/[\/\s]/g, '_')}.pdf`);
  };

  // ─── EXPORT EXCEL ─────────────────────────────────────────────────────────
  const handleExcel = () => {
    if (!data) return;
    const aoa = [];
    aoa.push([data.church?.name || 'Church']);
    if (data.church?.address) aoa.push([data.church.address]);
    const contact = [data.church?.phone, data.church?.email].filter(Boolean).join(' · ');
    if (contact) aoa.push([contact]);
    aoa.push([]);
    aoa.push(['WEEKLY CASH LOG']);
    aoa.push([`For the period ${fmtDateLong(data.period.start_date)} to ${fmtDateLong(data.period.end_date)}`]);
    aoa.push([]);
    aoa.push(['#', 'Day', 'Date', 'Receipt No.', 'Income Type', 'Category', 'Description', 'Method', 'Cheque No.', 'Recorded By', 'Amount (GH₵)']);

    weeks.forEach((wk, wi) => {
      aoa.push([`WEEK ${wi + 1}`, `${fmtDateShort(wk.start_date)} — ${fmtDateShort(wk.end_date)}`, '', '', '', '', '', '', '', '', Number(wk.total)]);
      wk.entries.forEach((r, i) => {
        aoa.push([
          i + 1,
          fmtDayName(r.income_date),
          r.income_date || '',
          r.receipt_no || '',
          r.income_type_name || '',
          r.income_category || '',
          r.narration || '',
          METHOD_LABEL[r.payment_method] || r.payment_method || '',
          r.cheque_no || '',
          r.recorded_by || '',
          Number(r.amount || 0),
        ]);
      });
      aoa.push(['', '', '', '', '', '', '', '', '', `Weekly Total`, Number(wk.total)]);
      aoa.push([]);
    });
    aoa.push(['', '', '', '', '', '', '', '', '', 'GRAND TOTAL', Number(grandTotal)]);

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [
      { wch: 5 }, { wch: 8 }, { wch: 12 }, { wch: 14 }, { wch: 22 },
      { wch: 18 }, { wch: 38 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 16 },
    ];
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r; R <= range.e.r; R++) {
      const addr = XLSX.utils.encode_cell({ r: R, c: 10 });
      const cell = ws[addr];
      if (cell && typeof cell.v === 'number') cell.z = '#,##0.00';
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Weekly Cash Log');
    const stamp = `${data.period.start_date}_to_${data.period.end_date}`;
    XLSX.writeFile(wb, `Weekly_Cash_Log_${stamp.replace(/[\/\s]/g, '_')}.xlsx`);
  };

  if (loading) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading weekly cash log for {fmtDateLong(start)} — {fmtDateLong(end)}…
        </Typography>
      </Box>
    );
  }
  if (error) return <Alert severity="error" onClose={() => setError('')}>{error}</Alert>;
  if (!data) return null;

  return (
    <Box>
      {/* Action bar */}
      <Stack direction="row" spacing={1} justifyContent="flex-end" mb={2} flexWrap="wrap">
        <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
        <Button size="small" variant="outlined" startIcon={<Print />} onClick={handlePrint} disabled={weeks.length === 0}>Print</Button>
        <Button size="small" variant="outlined" startIcon={<GridOn />} onClick={handleExcel} disabled={weeks.length === 0}
          sx={{ color: '#0F6C3D', borderColor: '#0F6C3D', '&:hover': { borderColor: '#0F6C3D', bgcolor: '#0F6C3D0d' } }}>
          Export Excel
        </Button>
        <Button size="small" variant="contained" startIcon={<PictureAsPdf />} onClick={handlePdf} disabled={weeks.length === 0}
          sx={{ bgcolor: RED, '&:hover': { bgcolor: '#991B1B' } }}>
          Export PDF
        </Button>
      </Stack>

      {/* Statement paper */}
      <Paper
        elevation={2}
        sx={{
          border: '1px solid #E2E8F0', borderRadius: 2,
          p: { xs: 2, md: 4 }, bgcolor: '#fff',
          maxWidth: 1280, mx: 'auto',
          boxShadow: '0 10px 30px rgba(15,23,42,0.06)',
        }}
      >
        <Box ref={printRef}>
          {/* Letterhead */}
          <Box className="letterhead" sx={{
            display: 'flex', alignItems: 'center', gap: 3,
            pb: 2, borderBottom: `3px double ${RULE}`,
          }}>
            {data.church?.logo ? (
              <Box component="img" src={data.church.logo} alt="Logo"
                sx={{ width: 84, height: 84, objectFit: 'contain', borderRadius: 1 }} />
            ) : (
              <Box sx={{
                width: 84, height: 84, borderRadius: 1,
                bgcolor: `${NAVY}12`, color: NAVY,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: 28,
              }}>
                {(data.church?.name || 'C').charAt(0).toUpperCase()}
              </Box>
            )}
            <Box className="info" sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h4" fontWeight={800}
                sx={{ color: NAVY, letterSpacing: '0.02em', lineHeight: 1.1 }}>
                {data.church?.name || 'Church'}
              </Typography>
              {(data.church?.address || data.church?.phone || data.church?.email) && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {[data.church?.address, data.church?.phone, data.church?.email].filter(Boolean).join('  ·  ')}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Title */}
          <Box className="stmt-title" sx={{ textAlign: 'center', mt: 3, mb: 1.5 }}>
            <Typography className="name" sx={{
              fontSize: 15, fontWeight: 800, letterSpacing: '0.16em', color: NAVY,
              borderTop: `1.5px solid ${NAVY}`, borderBottom: `1.5px solid ${NAVY}`,
              display: 'inline-block', px: 3, py: 0.5,
            }}>
              WEEKLY CASH LOG
            </Typography>
            <Typography className="period" variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              For the period <b>{fmtDateLong(data.period.start_date)}</b> to <b>{fmtDateLong(data.period.end_date)}</b>
            </Typography>
            {weeks.length > 0 && (
              <Stack direction="row" spacing={1} justifyContent="center" mt={1} flexWrap="wrap">
                <Chip size="small" label={`${weeks.length} week${weeks.length === 1 ? '' : 's'}`}
                  sx={{ bgcolor: `${NAVY}15`, color: NAVY, fontWeight: 700 }} />
                <Chip size="small" label={`${entryCount} entr${entryCount === 1 ? 'y' : 'ies'}`}
                  sx={{ bgcolor: `${GOLD}20`, color: '#6B4A08', fontWeight: 700 }} />
                <Chip size="small" label={`GH₵ ${fmt(grandTotal)}`}
                  sx={{ bgcolor: `${GREEN}15`, color: GREEN, fontWeight: 700, fontFamily: 'monospace' }} />
              </Stack>
            )}
          </Box>

          {/* Body */}
          {weeks.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
              <Book sx={{ fontSize: 40, color: '#c9d3e0', mb: 1 }} />
              <Typography variant="subtitle1" fontWeight={700}>No entries to display</Typography>
              <Typography variant="body2">There are no income entries in this period.</Typography>
            </Box>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 14 }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: 36 }}>#</th>
                  <th style={thStyle}>Day / Date</th>
                  <th style={thStyle}>Receipt No.</th>
                  <th style={thStyle}>Income Type</th>
                  <th style={thStyle}>Category</th>
                  <th style={thStyle}>Description</th>
                  <th style={thStyle}>Method</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Amount (GH₵)</th>
                </tr>
              </thead>
              <tbody>
                {weeks.map((wk, wi) => (
                  <React.Fragment key={wk.key}>
                    <tr>
                      <td colSpan={7} style={weekHeadStyle}>
                        WEEK {wi + 1} · {fmtDateShort(wk.start_date)} — {fmtDateShort(wk.end_date)}
                      </td>
                      <td style={weekHeadAmountStyle}>GH₵ {fmt(wk.total)}</td>
                    </tr>
                    {wk.entries.map((r, i) => (
                      <tr key={r.id} style={{ background: i % 2 === 1 ? '#fafbfc' : 'transparent' }}>
                        <td style={{ ...cellStyle, textAlign: 'center', color: '#94a3b8' }}>{i + 1}</td>
                        <td style={cellStyle}>
                          <span style={{ display: 'inline-block', minWidth: 34, color: '#64748b', fontSize: 11, fontWeight: 700 }}>
                            {fmtDayName(r.income_date)}
                          </span>
                          {fmtDateShort(r.income_date)}
                        </td>
                        <td style={{ ...cellStyle, fontWeight: 700, color: NAVY }}>{r.receipt_no || '—'}</td>
                        <td style={cellStyle}>{r.income_type_name || '—'}</td>
                        <td style={cellStyle}>
                          <span style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                            background: `${NAVY}12`, color: NAVY,
                            fontSize: 11, fontWeight: 700,
                          }}>{r.income_category || '—'}</span>
                        </td>
                        <td style={{ ...cellStyle, color: '#475569', maxWidth: 260 }}>{r.narration || '—'}</td>
                        <td style={cellStyle}>
                          <span style={methodChip(r.payment_method)}>
                            {METHOD_LABEL[r.payment_method] || r.payment_method || '—'}
                          </span>
                        </td>
                        <td style={amountStyle}>{fmt(r.amount)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan={7} style={weekTotalLabelStyle}>
                        Weekly Total ({wk.entries.length} entr{wk.entries.length === 1 ? 'y' : 'ies'})
                      </td>
                      <td style={weekTotalAmountStyle}>{fmt(wk.total)}</td>
                    </tr>
                    {wi < weeks.length - 1 && (
                      <tr><td colSpan={8} style={{ height: 12 }} /></tr>
                    )}
                  </React.Fragment>
                ))}
                <tr>
                  <td colSpan={7} style={grandLabelStyle}>
                    GRAND TOTAL ({entryCount} entries · {weeks.length} week{weeks.length === 1 ? '' : 's'})
                  </td>
                  <td style={grandAmountStyle}>{fmt(grandTotal)}</td>
                </tr>
              </tbody>
            </table>
          )}

          {/* Footer */}
          <Box className="footer" sx={{
            display: 'flex', justifyContent: 'space-between', mt: 4,
            fontSize: 10, color: '#888',
          }}>
            <span>Generated {new Date().toLocaleString('en-GH')}</span>
            <span>{data.church?.name || ''}</span>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

// ─── Inline styles ─────────────────────────────────────────────────────────
const thStyle = {
  textAlign: 'left', fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase',
  color: '#fff', background: NAVY, padding: '9px 10px', fontWeight: 700,
};
const cellStyle    = { padding: '6px 10px', fontSize: 12.5, borderBottom: '1px solid #eef0f4', color: '#1e293b' };
const amountStyle  = { padding: '6px 10px', fontSize: 13, borderBottom: '1px solid #eef0f4',
                       textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                       fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                       fontWeight: 700, color: '#0F766E' };
const weekHeadStyle = {
  background: NAVY, color: '#fff', fontWeight: 800, letterSpacing: '0.08em',
  padding: '8px 10px', fontSize: 12, textTransform: 'uppercase',
};
const weekHeadAmountStyle = {
  background: NAVY, color: '#fff', fontWeight: 800,
  padding: '8px 10px', textAlign: 'right',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
};
const weekTotalLabelStyle = {
  background: '#eef2f7', color: NAVY, fontWeight: 800,
  padding: '8px 10px', textTransform: 'uppercase', fontSize: 12,
  borderTop: `2px solid ${NAVY}`, borderBottom: `2px solid ${NAVY}`,
};
const weekTotalAmountStyle = {
  background: '#eef2f7', color: NAVY, fontWeight: 800,
  padding: '8px 10px', textAlign: 'right',
  borderTop: `2px solid ${NAVY}`, borderBottom: `2px solid ${NAVY}`,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
};
const grandLabelStyle = {
  padding: '12px 10px', fontSize: 13, fontWeight: 800, background: '#fffdf5',
  color: GREEN, borderTop: `3px double ${NAVY}`, borderBottom: `3px double ${NAVY}`,
  textTransform: 'uppercase', letterSpacing: '0.08em',
};
const grandAmountStyle = {
  padding: '12px 10px', fontSize: 14, fontWeight: 800, textAlign: 'right',
  background: '#fffdf5', color: GREEN,
  borderTop: `3px double ${NAVY}`, borderBottom: `3px double ${NAVY}`,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
};

const methodChip = (method) => {
  const map = {
    cash:         { bg: '#e7f5ef', fg: '#0F766E' },
    cheque:       { bg: '#eef2f7', fg: NAVY },
    transfer:     { bg: '#e6f0fb', fg: '#1565C0' },
    mobile_money: { bg: '#fff4de', fg: '#8a5a08' },
  };
  const c = map[method] || { bg: '#f1f5f9', fg: '#475569' };
  return {
    display: 'inline-block', padding: '2px 8px', borderRadius: 10,
    background: c.bg, color: c.fg, fontSize: 11, fontWeight: 700,
  };
};
